/* eslint-disable react/prop-types */
import {
  Alert,
  Button,
  Collapse,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Space,
  Spin,
  TimePicker,
  Tooltip,
  message,
} from "antd";
import dayjs from "dayjs";
// eslint-disable-next-line no-unused-vars
import React, { useEffect, useState } from "react";
// import styled from "styled-components";
import { t } from "ttag";

import LoadingSpinner from "metabase/common/components/LoadingSpinner";
import Label from "metabase/common/components/type/Label";
import { FormButton } from "metabase/jerry/style/FormButton.styled";
import {
  DetailItem,
  PanelHeader,
} from "metabase/jerry/style/ScheduleSpreadsheet.styled";
import { openInBlankWindow } from "metabase/lib/dom";
import { Icon } from "metabase/ui";

const { Option } = Select;

const apiRequest = async (type, payload) => {
  try {
    const response = await fetch(`/api/jerry/extend`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        call: "post",
        service_name: "spreadsheet",
        body: { type, payload },
        timeout: 120000,
      }),
    });
    const responseData = await response.json();
    if (!response.ok || responseData.status !== true) {
      throw new Error(responseData.message || `API request failed.`);
    }
    return responseData;
  } catch (error) {
    message.error(error.message || "An unexpected error occurred.");
    throw error;
  }
};

const fetchHistoryAPI = (questionId) =>
  apiRequest("get_schedule_list", {
    report_type: "report",
    question_id: questionId,
  });
const updateScheduleAPI = (payload) => apiRequest("update_schedule", payload);
const deleteScheduleAPI = (payload) => apiRequest("delete_schedule", payload);
const testScheduleAPI = (payload) => apiRequest("test_schedule", payload);
const getSpreadsheetAPI = (payload) => apiRequest("get_spreadsheet", payload);

function ScheduleEditForm({ item, users, onSave, onTest, onDelete }) {
  const [form] = Form.useForm();
  const [scheduleType, setScheduleType] = useState(
    item?.submission_value?.schedule?.type || "daily",
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isFetchingLink, setIsFetchingLink] = useState(false);

  useEffect(() => {
    const submissionValue = item?.submission_value || {};
    const schedule = submissionValue?.schedule || {};

    form.setFieldsValue({
      ...submissionValue,
      scheduleType: schedule.type,
      time: schedule.time ? dayjs(schedule.time, "HH:mm") : null,
      dayOfWeek: schedule.dayOfWeek,
      dayOfMonth: schedule.dayOfMonth,
    });
    setScheduleType(schedule.type || "daily");
  }, [item, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setIsSaving(true);
      await onSave(item.file_name, values);
    } catch (error) {
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      const values = await form.validateFields();
      setIsTesting(true);
      await onTest(item.file_name, values);
    } catch (error) {
    } finally {
      setIsTesting(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(item.file_name);
    } catch (error) {
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFetchAndOpenLink = async () => {
    setIsFetchingLink(true);
    try {
      const payload = {
        spreadsheetName: item?.submission_value?.spreadsheetName,
        emails: item?.submission_value.recipients,
      };
      const result = await getSpreadsheetAPI(payload);
      const spreadsheetUrl = result?.spreadsheet.data?.spreadsheet?.url;

      if (spreadsheetUrl) {
        window.open(spreadsheetUrl, "_blank", "noopener,noreferrer");
      } else {
        message.warning(
          t`Spreadsheet URL is not available for this schedule yet.`,
        );
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    } finally {
      setIsFetchingLink(false);
    }
  };

  const spreadsheetNameLabel = (
    <Space>
      <span>{t`Spreadsheet Name`}</span>
      <Tooltip title={t`Get and open Google Spreadsheet link`}>
        <span
          onClick={handleFetchAndOpenLink}
          style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
        >
          {isFetchingLink ? <LoadingSpinner size={16} /> : <Icon name="info" />}
        </span>
      </Tooltip>
    </Space>
  );

  const renderScheduleOptions = () => (
    <Space>
      {scheduleType === "weekly" && (
        <Form.Item name="dayOfWeek" noStyle rules={[{ required: true }]}>
          <Select placeholder="Select day" style={{ width: 120 }}>
            <Option value="monday">{t`Monday`}</Option>
            <Option value="tuesday">{t`Tuesday`}</Option>
            <Option value="wednesday">{t`Wednesday`}</Option>
            <Option value="thursday">{t`Thursday`}</Option>
            <Option value="friday">{t`Friday`}</Option>
            <Option value="saturday">{t`Saturday`}</Option>
            <Option value="sunday">{t`Sunday`}</Option>
          </Select>
        </Form.Item>
      )}
      {scheduleType === "monthly" && (
        <Form.Item name="dayOfMonth" noStyle rules={[{ required: true }]}>
          <Select placeholder="Select day" style={{ width: 120 }}>
            {[...Array(28).keys()].map((i) => (
              <Option key={i + 1} value={i + 1}>{`Day ${i + 1}`}</Option>
            ))}
          </Select>
        </Form.Item>
      )}
      <Form.Item name="time" noStyle rules={[{ required: true }]}>
        <TimePicker format="HH:mm" />
      </Form.Item>
    </Space>
  );

  return (
    <Form form={form} layout="vertical">
      {item.updated_at && (
        <DetailItem>
          <strong>{t`Last Updated At:`}</strong>
          <span>{dayjs(item.updated_at).format("YYYY-MM-DD HH:mm")}</span>
        </DetailItem>
      )}
      {item.updated_user && (
        <DetailItem>
          <strong>{t`Last Updated By:`}</strong>
          <span>{item.updated_user?.common_name}</span>
        </DetailItem>
      )}

      {item.url && (
        <DetailItem>
          <strong>{t`Source Question:`}</strong>
          <span>
            <a href={item.url} target="_blank" rel="noopener noreferrer">
              {item.url}
            </a>
          </span>
        </DetailItem>
      )}

      <Form.Item
        name="spreadsheetName"
        label={spreadsheetNameLabel}
        rules={[{ required: true }]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        name="recipients"
        label={t`Notify Users`}
        rules={[{ required: true, type: "array", min: 1 }]}
      >
        <Select mode="multiple" placeholder="Select users">
          {users.map((u) => (
            <Option key={u.email} value={u.email}>
              {u.common_name}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item label={t`Schedule (UTC Timezone)`}>
        <Space.Compact>
          <Form.Item name="scheduleType" noStyle>
            <Radio.Group onChange={(e) => setScheduleType(e.target.value)}>
              <Radio.Button value="daily">{t`Daily`}</Radio.Button>
              <Radio.Button value="weekly">{t`Weekly`}</Radio.Button>
              <Radio.Button value="monthly">{t`Monthly`}</Radio.Button>
            </Radio.Group>
          </Form.Item>
          {renderScheduleOptions()}
        </Space.Compact>
      </Form.Item>

      <Form.Item>
        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
          <Popconfirm
            title={t`Are you sure you want to delete this schedule?`}
            onConfirm={handleDelete}
            okText={t`Yes`}
            cancelText={t`No`}
          >
            <Button danger loading={isDeleting}>{t`Delete`}</Button>
          </Popconfirm>
          <Button loading={isTesting} onClick={handleTest}>{t`Test`}</Button>
          <Button
            type="primary"
            loading={isSaving}
            onClick={handleSave}
          >{t`Save`}</Button>
        </Space>
      </Form.Item>
    </Form>
  );
}

function ScheduleSpreadsheetHistory({
  question,
  user,
  users = [],
  result,
  onModalStateChange,
}) {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const [alertInfo, setAlertInfo] = useState({
    visible: false,
    message: "",
    url: "",
  });

  useEffect(() => {
    if (onModalStateChange) {
      onModalStateChange(isModalVisible);
    }
  }, [isModalVisible, onModalStateChange]);

  const handleCollapseChange = () => {
    if (alertInfo.visible) {
      setAlertInfo({ visible: false, message: "", url: "" });
    }
  };

  const loadHistory = async () => {
    handleCollapseChange();
    setIsLoading(true);
    try {
      const result = await fetchHistoryAPI(question.id());
      const sortedData = result.data.sort((a, b) =>
        dayjs(b.created_at).diff(dayjs(a.created_at)),
      );
      setHistory(sortedData);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const showHistoryModal = () => {
    setIsModalVisible(true);
    loadHistory();
  };

  const handleSave = async (fileName, values) => {
    const payload = {
      file_name: fileName,
      report_type: "report",
      question_id: question.id(),
      updated_user: user,
      updated_at: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      submission_value: {
        spreadsheetName: values.spreadsheetName,
        recipients: values.recipients,
        schedule: {
          type: values.scheduleType,
          time: values.time.format("HH:mm"),
          dayOfWeek: values.dayOfWeek,
          dayOfMonth: values.dayOfMonth,
        },
      },
    };
    const responseData = await updateScheduleAPI(payload);

    loadHistory();

    setAlertInfo({
      visible: true,
      message: t`Saved successfully!`,
      url: responseData.url,
    });
    openInBlankWindow(responseData.url);
  };

  const handleTest = async (fileName, values) => {
    const payload = {
      ...values,
      file_name: fileName,
      data: result?.data,
      user: user,
      emails: values.recipients,
    };
    await testScheduleAPI(payload);
    message.success("Test email sent successfully!");
  };

  const handleDelete = async (fileName) => {
    await deleteScheduleAPI({
      file_name: fileName,
      report_type: "report",
      question_id: question.id(),
    });
    message.success("Schedule deleted successfully!");
    setHistory((currentHistory) =>
      currentHistory.filter((item) => item.file_name !== fileName),
    );
  };

  const collapseItems = history.map((item) => ({
    key: item.file_name,
    label: (
      <PanelHeader>
        {/*<span>{item.submission_value?.spreadsheetName}</span>*/}
        <span>
          {t`Created by ${item.created_user?.common_name} on ${dayjs(
            item.created_at,
          ).format("YYYY-MM-DD")}`}
        </span>
      </PanelHeader>
    ),
    children: (
      <ScheduleEditForm
        item={item}
        users={users}
        onSave={handleSave}
        onTest={handleTest}
        onDelete={handleDelete}
      />
    ),
  }));

  return (
    <>
      <div style={{ width: "100%" }}>
        <FormButton
          onClick={showHistoryModal}
          className="hover-parent hover--inherit"
        >
          <Icon
            name="history"
            size={30}
            style={{
              marginLeft: "2px",
              marginRight: "10px",
              color: "#949097",
            }}
          />
          <Label
            my={0}
            style={{ marginTop: "5px" }}
          >{t`Schedule History`}</Label>
        </FormButton>
      </div>
      <Modal
        title={t`Scheduled Export History`}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsModalVisible(false)}>
            {t`Close`}
          </Button>,
        ]}
        width="700px"
      >
        <Spin spinning={isLoading} tip="Loading history...">
          {alertInfo.visible && (
            <Alert
              message={alertInfo.message}
              description={
                <>
                  {t`Your Metabase export to Google Spreadsheet subscription has been updated.`}{" "}
                  <a
                    href={alertInfo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t`Click to view`}
                  </a>
                </>
              }
              type="success"
              showIcon
              closable
              onClose={() =>
                setAlertInfo({ visible: false, message: "", url: "" })
              }
              style={{ marginBottom: "16px" }}
            />
          )}

          {!isLoading && history.length === 0 ? (
            <Empty
              description={t`No scheduled exports have been configured.`}
            />
          ) : (
            <Collapse
              accordion
              items={collapseItems}
              onChange={handleCollapseChange}
            />
          )}
        </Spin>
      </Modal>
    </>
  );
}

export default ScheduleSpreadsheetHistory;
